import { Injectable } from '@angular/core';
import { CourseListMemento } from './course-list-memento.interface';
import { logError } from '../logging/logger.service';

@Injectable({
  providedIn: 'root',
})
export class CourseListMementoService {
  private readonly STORAGE_KEY = 'courseListMemento';
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Save the complete course list state
   */
  saveState(memento: CourseListMemento): void {
    try {
      const stateWithTimestamp = {
        ...memento,
        timestamp: Date.now(),
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(stateWithTimestamp));
    } catch (error) {
      logError('core/services/course-list-memento.service', 'Failed to save course list state:', error);
    }
  }

  /**
   * Restore the complete course list state
   */
  restoreState(): CourseListMemento | null {
    try {
      const savedState = localStorage.getItem(this.STORAGE_KEY);
      if (!savedState) {
        return null;
      }

      const memento: CourseListMemento = JSON.parse(savedState);

      // Check if cache is still valid
      if (this.isCacheExpired(memento.timestamp)) {
        this.clearState();
        return null;
      }

      return memento;
    } catch (error) {
      logError('core/services/course-list-memento.service', 'Failed to restore course list state:', error);
      this.clearState();
      return null;
    }
  }

  /**
   * Clear the saved state
   */
  clearState(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      logError('core/services/course-list-memento.service', 'Failed to clear course list state:', error);
    }
  }

  /**
   * Check if there's a saved state available
   */
  hasSavedState(): boolean {
    try {
      const savedState = localStorage.getItem(this.STORAGE_KEY);
      if (!savedState) return false;

      const memento: CourseListMemento = JSON.parse(savedState);
      return !this.isCacheExpired(memento.timestamp);
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if the cached state has expired
   */
  private isCacheExpired(timestamp: number): boolean {
    return Date.now() - timestamp > this.CACHE_DURATION;
  }
}
